import { store } from '@/store'
import { defineStore } from 'pinia'
import dayjs from 'dayjs'
import { getAccessToken, removeToken } from '@/utils/auth'
import { CACHE_KEY, useCache, deleteUserCache } from '@/hooks/web/useCache'
import { loginOut as loginOutApi } from '@/api/login'
import { getUserInfo, type UserInfo } from '@/api/txn/user'
import { getCompanyList, type CompanyInfo } from '@/api/txn/company'

const { wsCache } = useCache()

interface UserVO {
  id: number
  avatar: string
  nickname: string
  deptId: number
  email: string
  mobile?: string
  // 经纪人相关
  brokerBindFlag?: boolean
  brokerId?: number
  brokerBindTime?: number
  brokerAuthFlag?: boolean
  brokerAuthTime?: number
  /** 是否拥有已认证的企业（来自 /txn/member/get，避免首页循环企业列表判断） */
  hasVerifiedCompany?: boolean
}

interface UserInfoVO {
  isSetUser: boolean
  user: UserVO
  organization: OrganizationVO
  /** 是否经纪人登录 */
  isBrokerLogin: boolean
  /** 经纪人绑定的电力用户列表（供切换用户用） */
  brokerMemberList: any[]
  /** 经纪人信息 */
  brokerInfo: any
  /** 经纪人 token（用于切换电力用户） */
  brokerToken: string
}

export interface OrganizationVO {
  // ID
  id?: number

  // --- 基础信息 ---
  orgLicenseUrl?: string
  orgName?: string
  orgCertType?: number
  orgCertNum?: string
  legalRepName?: string
  legalRepCertType?: number
  legalRepCertNum?: string
  orgMobile?: string
  orgLandline?: string
  orgContactReserve?: string

  // --- 企业对公账户 ---
  corpAccountName?: string
  corpBankName?: string
  corpBankBranchName?: string
  corpBankAccount?: string
  bankUnionCode?: string

  // --- 企业联系人信息 ---
  contactName?: string
  contactPhone?: string
  remark?: string
  intro?: string

  // --- 认证信息 ---
  authStatus?: boolean
  authFlowStatus?: number
  authFlowRejectReason?: string
  authFlowSubmitTime?: string
  authFlowAuditTime?: string
  adminUserId?: number
}

/** 将 dzh-app 用户信息映射为 UserVO */
function mapUserInfo(data: UserInfo): UserVO {
  return {
    id: data.id,
    avatar: data.avatar || '',
    nickname: data.nickname || '',
    deptId: 0,
    email: data.email || '',
    mobile: data.mobile || '',
    brokerBindFlag: data.brokerBindFlag,
    brokerId: data.brokerId,
    brokerBindTime: data.brokerBindTime,
    brokerAuthFlag: data.brokerAuthFlag,
    brokerAuthTime: data.brokerAuthTime,
    hasVerifiedCompany: data.hasVerifiedCompany,
  }
}

/** 将 dzh-app 企业信息映射为 OrganizationVO */
function mapCompanyToOrg(data: CompanyInfo): OrganizationVO {
  return {
    id: data.id,
    orgName: data.companyName,
    orgLicenseUrl: data.licenseImgUrl,
    orgCertNum: data.licenseNo,
    legalRepName: data.legalPerson,
    contactName: data.contactName,
    contactPhone: data.contactMobile,
    authStatus: data.authStatus,
    authFlowStatus: data.authFlowStatus,
    authFlowRejectReason: data.authFlowRejectReason,
    authFlowSubmitTime: data.authFlowSubmitAt,
    authFlowAuditTime: data.authFlowAuditAt,
  }
}

export const useUserStore = defineStore('admin-user', {
  state: (): UserInfoVO => ({
    isSetUser: false,
    user: {
      id: 0,
      avatar: '',
      nickname: '',
      deptId: 0,
      email: '',
      mobile: '',
    },
    organization: {
      id: 0,
    },
    isBrokerLogin: false,
    brokerMemberList: [],
    brokerInfo: null,
    brokerToken: '',
  }),
  getters: {
    getIsSetUser(): boolean {
      return this.isSetUser
    },
    getUser(): UserVO {
      return this.user
    },
    getEnte(): OrganizationVO {
      return this.organization
    },
    getIsLogin(): boolean {
      return !!getAccessToken() && this.isSetUser
    },
    /** 是否经纪人登录 */
    getIsBrokerLogin(): boolean {
      return this.isBrokerLogin
    },
    /** 经纪人头像 */
    brokerAvatar(): string {
      return this.user.avatar || ''
    },
    /** 脱敏手机号 */
    maskedMobile(): string {
      const m = this.user.mobile
      if (!m) return ''
      return m.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
    },
    /** 经纪人绑定时间 */
    bindTime(): string {
      return this.user.brokerBindTime ? dayjs(this.user.brokerBindTime).format('YYYY-MM-DD HH:mm') : ''
    },
    /** 经纪人授权时间 */
    authTime(): string {
      return this.user.brokerAuthTime ? dayjs(this.user.brokerAuthTime).format('YYYY-MM-DD HH:mm') : ''
    },
  },
  actions: {
    /** 登录后获取用户信息 + 企业信息（走 dzh-app 接口体系） */
    async setUserInfoAction() {
      if (!getAccessToken()) {
        this.resetState()
        return null
      }
      // 尝试从缓存恢复
      const user = wsCache.get(CACHE_KEY.USER) as UserVO | null
      const org = wsCache.get('APP_ORGANIZATION') as OrganizationVO | null

      if (user && org) {
        // 有缓存：直接恢复，同时后台静默刷新
        this.user = user
        this.organization = org
        this.isSetUser = true
        // 恢复经纪人登录状态
        const brokerData = wsCache.get('APP_BROKER_LOGIN') as { isBrokerLogin: boolean; brokerMemberList: any[]; brokerInfo: any; brokerToken: string } | null
        if (brokerData) {
          this.isBrokerLogin = brokerData.isBrokerLogin
          this.brokerMemberList = brokerData.brokerMemberList || []
          this.brokerInfo = brokerData.brokerInfo || null
          this.brokerToken = brokerData.brokerToken || ''
        }
        // 静默刷新（不阻塞）
        this.refreshUserAndOrg().catch(() => {})
      } else {
        // 无缓存：等待接口返回
        await this.refreshUserAndOrg()
        this.isSetUser = true
      }
    },
    /** 从接口刷新用户和企业信息 */
    async refreshUserAndOrg() {
      try {
        // 并行请求用户信息和企业列表
        const [userRes, companyListRes] = await Promise.allSettled([
          getUserInfo(),
          getCompanyList(),
        ])

        // 处理用户信息
        if (userRes.status === 'fulfilled') {
          this.user = mapUserInfo(userRes.value)
          wsCache.set(CACHE_KEY.USER, this.user)
        }

        // 处理企业信息（优先取第一个已认证的企业；都没有则取第一家）
        // 字典 txn_auth_flow_status：0=未认证 1=认证中 2=已认证 3=已驳回
        // 与 my/company、my/index、CompanyDetailDialog 的判断条件保持一致
        if (companyListRes.status === 'fulfilled' && companyListRes.value?.length > 0) {
          const verifiedCompany =
            companyListRes.value.find((c) => c.authStatus === true || c.authFlowStatus === 2) ||
            companyListRes.value[0]
          this.organization = mapCompanyToOrg(verifiedCompany)
        } else {
          this.organization = { id: 0 }
        }
        wsCache.set('APP_ORGANIZATION', this.organization)
      } catch (error) {
        // 接口失败时使用默认值，保证可以进入系统
        if (!this.user.id) {
          this.user = { id: 0, avatar: '', nickname: '', deptId: 0, email: '', mobile: '' }
        }
        if (!this.organization.id) {
          this.organization = { id: 0 }
        }
      }
    },
    async setUserAvatarAction(avatar: string) {
      this.user.avatar = avatar
      wsCache.set(CACHE_KEY.USER, this.user)
    },
    async setUserNicknameAction(nickname: string) {
      this.user.nickname = nickname
      wsCache.set(CACHE_KEY.USER, this.user)
    },
    async loginOut() {
      try {
        await loginOutApi()
      } catch {}
      removeToken()
      deleteUserCache()
      wsCache.delete('APP_ORGANIZATION')
      wsCache.delete('APP_BROKER_LOGIN')
      this.resetState()
    },
    /** 设置经纪人登录信息 */
    setBrokerLoginInfo(data: { memberList: any[]; brokerInfo: any; brokerToken: string }) {
      this.isBrokerLogin = true
      this.brokerMemberList = data.memberList || []
      this.brokerInfo = data.brokerInfo || null
      this.brokerToken = data.brokerToken || ''
      wsCache.set('APP_BROKER_LOGIN', {
        isBrokerLogin: true,
        brokerMemberList: this.brokerMemberList,
        brokerInfo: this.brokerInfo,
        brokerToken: this.brokerToken,
      })
    },
    resetState() {
      this.isSetUser = false
      this.user = {
        id: 0,
        avatar: '',
        nickname: '',
        deptId: 0,
        email: '',
        mobile: '',
      }
      this.organization = { id: 0 }
      this.isBrokerLogin = false
      this.brokerMemberList = []
      this.brokerInfo = null
      this.brokerToken = ''
    },
  },
})

export const useUserStoreWithOut = () => {
  return useUserStore(store)
}
